import { IProductDetail } from '@/api/types/public';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { randomId } from '@/utils/tools';
import { Box, Collapse } from '@mui/material';
import { MinusOutlined, PlusOutlined, QuestionOutlined, WapRightOutlined } from '@zeelool/icon';
import { useTranslations } from 'next-intl';
import React, { useContext, useState } from 'react';
import Popup from '../Popup';
import { useLensesAndCoatings } from './hooks/useLensesAndCoatings';
import LensDetails from './LensDetails';

// 镜片类型项目组件
const LensTypeItem: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { deviceType } = useContext(ResponsiveContext);
  return (
    <Box className="flex items-center">
      <WapRightOutlined
        className={`translate-y-[-1px] mr-8 text-[${deviceType === EPlatform.WEB ? '20px' : '14px'}]`}
      />
      <Box
        component="span"
        className="ml-2 text-[13px] text-primary-text font-normal leading-[1.3] whitespace-nowrap"
      >
        {children}
      </Box>
    </Box>
  );
};

interface LenseAvailableProps {
  detail: IProductDetail;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
  defaultOpen?: boolean;
  showQuestionIcon?: boolean;
}

const LenseAvailable: React.FC<LenseAvailableProps> = ({
  detail,
  SDProductDetailsPageBtnClick,
  defaultOpen = true,
  showQuestionIcon = false,
}) => {
  const t = useTranslations('goodsDetail.lensesAndCoatings');
  const { deviceType } = useContext(ResponsiveContext);
  const { availableItems, showThisModule } = useLensesAndCoatings(detail);
  const [isExpanded, setIsExpanded] = useState(defaultOpen);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  if (!showThisModule) {
    return null;
  }

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleQuestionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPopupOpen(true);
    SDProductDetailsPageBtnClick?.('Lenses&Coating');
  };

  return (
    <>
      <div className="relative bg-white">
        {/* 标题栏 */}
        <div
          className="flex items-center justify-between h-[50px] leading-[50px] border-t border-[#eeeeee] cursor-pointer"
          onClick={handleToggle}
        >
          <div className="flex items-center">
            <h5 className="text-[14px] font-bb">{t('titleNew')}</h5>
            {showQuestionIcon && (
              <QuestionOutlined
                className="ml-5 text-[14px] text-[#808080] cursor-pointer"
                onClick={handleQuestionClick}
              />
            )}
          </div>
          {isExpanded ? (
            <MinusOutlined className="text-[16px]" style={{ color: '#282828' }} />
          ) : (
            <PlusOutlined className="text-[16px]" style={{ color: '#282828' }} />
          )}
        </div>

        {/* 内容区域 */}
        <Collapse in={isExpanded}>
          <Box
            className={`grid grid-cols-2 gap-y-[10px] lenses-wrap ${
              deviceType === EPlatform.WEB ? 'mb-[24px]' : 'mb-[20px]'
            }`}
          >
            {availableItems.map((item) => (
              <Box className="col-span-1" key={randomId()}>
                <LensTypeItem>{item}</LensTypeItem>
              </Box>
            ))}
          </Box>
        </Collapse>
      </div>

      {/* 详情弹窗 */}
      <Popup
        open={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        title={t('titleNew')}
        position="bottom"
        height="80vh"
      >
        <LensDetails detail={detail} variant="inPopup" />
      </Popup>
    </>
  );
};

export default LenseAvailable;
